/**
 * DonationCaptureFailed.tsx - Donation Failure Handling Step
 * 
 * This component displays error information when a donation fails to process
 * with helpful error messages and retry options for users.
 * 
 * Key Features:
 * - Clear error messaging and explanation
 * - Retry options with different payment methods
 * - Support contact information
 * - Error categorization (payment, network, validation)
 * - User-friendly error descriptions
 * - Navigation options for recovery
 * 
 * Error Types:
 * - Payment Declined: Card or bank account issues
 * - Network Error: Connection or timeout issues
 * - Validation Error: Form or data validation failures
 * - System Error: Internal processing errors
 * 
 * Recovery Options:
 * - Retry with same payment method
 * - Try different payment method
 * - Contact support for assistance
 * - Return to fundraiser page
 * - Start new donation process
 * 
 * Props:
 * - error: DonationError - Error information and type
 * - onRetry: () => void - Retry donation handler
 * - onChangePayment: () => void - Change payment method handler
 * - onClose: () => void - Close error modal handler
 * - onContactSupport: () => void - Contact support handler
 * 
 * Dependencies: Error types, support integration, styled-components
 */

import React from 'react';
import styled from 'styled-components';
import { AlertCircle, X } from 'lucide-react';
import PaymentFailedImage from "../../images//payment-failed.svg";
const FailedContainer = styled.div<{ isEmbedded: boolean }>`
  background: #ffffff;
  border-radius: ${props => props.isEmbedded ? '16px' : '24px'};
  width: 100%;
  max-width: ${props => props.isEmbedded ? '100%' : '600px'};
  min-height: ${props => props.isEmbedded ? '660px' : 'auto'};
  max-height: ${props => props.isEmbedded ? 'none' : '90vh'};
  overflow-y: ${props => props.isEmbedded ? 'visible' : 'auto'};
  position: relative;
  box-shadow: ${props => props.isEmbedded ? '0 4px 20px rgba(0, 0, 0, 0.08)' : '0 20px 60px rgba(0, 0, 0, 0.3)'};
  border: ${props => props.isEmbedded ? '1px solid #f1f5f9' : '1px solid rgba(255, 255, 255, 0.2)'};
  display: flex;
  flex-direction: column;
`;

const HeaderSection = styled.div<{ isEmbedded: boolean }>`
  background: #fef2f2;
  border-radius: ${props => props.isEmbedded ? '16px 16px 0 0' : '24px 24px 0 0'};
  padding: ${props => props.isEmbedded ? '32px 32px' : '24px 32px'};
  text-align: center;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: #64748b;
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const FailedTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #ef4444;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
`;

const FailedContent = styled.div`
  padding: 8px 32px;
  display: flex;
  flex-direction: column;
`;

const AmountSection = styled.div`
  text-align: center;
  margin-bottom: 24px;
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const AmountValue = styled.div`
  font-size: 48px;
  font-weight: 800;
  color: #ef4444;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FailedIcon = styled.img`
  width: 176px;
  height: auto;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 16px 40px;
  margin-bottom: 24px;
  align-items: start;
`;

const DetailLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  text-align: right;
`;

const DetailValue = styled.div`
  font-size: 16px;
  color: #1e293b;
  font-weight: 500;
  line-height: 1.4;
  text-align: left;
`;

const PaymentMethodContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaymentMethodIcon = styled.div<{ cardType?: string }>`
  width: 32px;
  height: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  
  ${props => {
    switch (props.cardType) {
      case 'visa':
        return 'background: #1A1F71;';
      case 'mastercard':
        return 'background: #EB001B;';
      case 'amex':
        return 'background: #006FCF;';
      case 'discover':
        return 'background: #FF6000;';
      default:
        return 'background: #1A1F71;';
    }
  }}
`;

const PaymentAccount = styled.span``;

const PaymentType = styled.span``;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.5;
  margin: 21px auto 3px auto;
  text-align: center;
  max-width: 400px;
`;

const NewPaymentButton = styled.button`
  background: #ef4444;
  color: #ffffff;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0;

  &:hover {
    background: #dc2626;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(239, 68, 68, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const FooterContainer = styled.div<{ isEmbedded: boolean }>`
  display: flex;
  gap: 16px;
  justify-content: center;
  align-items: center;
  padding: ${props => props.isEmbedded ? '0' : '24px 0 16px 0'};
  border-top: ${props => props.isEmbedded ? 'none' : '1px solid #f1f5f9'};
  margin-top: ${props => props.isEmbedded ? '24px' : '16px'};
`;

const CloseModalButton = styled.button<{ isEmbedded: boolean }>`
  padding: 16px 24px;
  background: #ffffff;
  color: #64748b;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: ${props => props.isEmbedded ? 'none' : 'flex'};

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

interface DonationData {
  amount: number;
  frequency: 'one-time' | 'monthly';
  donorInfo: {
    firstName: string;
    lastName: string;
    streetAddress: string;
    zip: string;
    city: string;
    state: string;
    country: string;
    email: string;
    phone: string;
  };
  //paymentMethod: 'card' | 'bank';
  // paymentInfo: {
  //   cardNumber: string;
  //   accountHolderName: string;
  // };
}

interface DonationCaptureFailedProps {
  donationData: DonationData;
  onNewPaymentMethod: () => void;
  isEmbedded?: boolean;
}

export const DonationCaptureFailed: React.FC<DonationCaptureFailedProps> = ({
  donationData,
  onNewPaymentMethod,
  isEmbedded = false
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  return (
    <FailedContainer isEmbedded={isEmbedded}>

      <HeaderSection isEmbedded={isEmbedded}>
        <FailedTitle>
          <AlertCircle size={28} color="#ef4444" />
          Payment could not be processed
        </FailedTitle>
      </HeaderSection>

      <FailedContent>
        <AmountSection>
          <AmountValue>
            {formatCurrency(donationData.amount)}
          </AmountValue>
          <FailedIcon src={PaymentFailedImage} alt="Payment Failed" />
        </AmountSection>

        <DetailsGrid>
          <DetailLabel>Name</DetailLabel>
          <DetailValue>
            {donationData.donorInfo.firstName} {donationData.donorInfo.lastName}
          </DetailValue>

          <DetailLabel>Date</DetailLabel>
          <DetailValue>{formatDate(new Date())}</DetailValue>

          <DetailLabel>Amount</DetailLabel>
          <DetailValue>{formatCurrency(donationData.amount)} USD</DetailValue>

          <DetailLabel>Frequency</DetailLabel>
          <DetailValue>
            {donationData.frequency === 'monthly' ? 'Monthly' : 'One Time'}
          </DetailValue>

          {/* <DetailLabel>Payment Method</DetailLabel>
          <DetailValue>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {donationData.paymentMethod === 'bank' && (
                <img src="/bank-icon.svg" alt="Bank" width={64} height={80} />
              )}
              {donationData.paymentMethod === 'bank' ? '...123456' : getPaymentMethodDisplay()}
            </div>
          </DetailValue> */}
        </DetailsGrid>

        {/* <ErrorMessage>
          Your donation is very important. Please click below{'\n'}
          to provide a new payment method.
        </ErrorMessage> */}
      </FailedContent>

      <FooterContainer isEmbedded={isEmbedded}>
        <NewPaymentButton onClick={onNewPaymentMethod}>
          New Payment Method
        </NewPaymentButton>
        <CloseModalButton
          isEmbedded={isEmbedded}
          onClick={onNewPaymentMethod}
          style={{ display: isEmbedded ? 'none' : 'flex' }}
        >
          Close
        </CloseModalButton>
      </FooterContainer>
    </FailedContainer>
  );
};